import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { fetchEmployeeReport } from '../lib/api';
import { getReportData } from '../lib/store';
import { computeEmployeeDetail } from '../lib/employeeDetail';
import {
  WeekAvailability,
  EntriesTable,
  DailyHoursChart,
  WeeklyTrendChart,
  ClientHoursCharts,
} from './EmployeeDetailSections';
import {
  getFirstDayOfMonth,
  getLastDayOfMonth,
  getWeekStartEnd,
  getWeeksInYear,
  getISOWeek,
  formatDate,
  formatDisplayDate,
} from '../lib/dateUtils';
import type { EmployeeReport } from '../data/types';

interface MyPerformanceProps {
  employeeId: string;
}

type ViewMode = 'acumulado' | 'mensual' | 'semanal';

const MONTHS = [
  { value: 1, label: 'Enero' },
  { value: 2, label: 'Febrero' },
  { value: 3, label: 'Marzo' },
  { value: 4, label: 'Abril' },
  { value: 5, label: 'Mayo' },
  { value: 6, label: 'Junio' },
  { value: 7, label: 'Julio' },
  { value: 8, label: 'Agosto' },
  { value: 9, label: 'Septiembre' },
  { value: 10, label: 'Octubre' },
  { value: 11, label: 'Noviembre' },
  { value: 12, label: 'Diciembre' },
];

const YEARS = [2025, 2026, 2027];

// Mismos cortes de color que AreaCard: Verde ≥ 100% · Amarillo 80–99% · Rojo < 80%
const COLOR_VERDE_MIN = 1.0;
const COLOR_AMARILLO_MIN = 0.8;

function cellColor(value: number): string {
  if (value >= COLOR_VERDE_MIN) return 'bg-green-200 text-green-900';
  if (value >= COLOR_AMARILLO_MIN) return 'bg-amber-100 text-amber-900';
  return 'bg-red-200 text-red-900';
}

function formatPct(value: number): string {
  return `${Math.round(value * 100)}%`;
}

export function MyPerformance({ employeeId }: MyPerformanceProps) {
  const [viewMode, setViewMode] = useState<ViewMode>('acumulado');

  const today = useMemo(() => new Date(), []);
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [week, setWeek] = useState(() => getISOWeek(today).week);

  const weeks = useMemo(() => getWeeksInYear(year), [year]);

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow p-6">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex bg-slate-100 rounded-lg p-1">
            <button
              type="button"
              onClick={() => setViewMode('acumulado')}
              className={`px-4 py-1.5 rounded-md text-sm font-medium transition ${
                viewMode === 'acumulado'
                  ? 'bg-white text-slate-900 shadow'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              Acumulado
            </button>
            <button
              type="button"
              onClick={() => setViewMode('mensual')}
              className={`px-4 py-1.5 rounded-md text-sm font-medium transition ${
                viewMode === 'mensual'
                  ? 'bg-white text-slate-900 shadow'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              Mensual
            </button>
            <button
              type="button"
              onClick={() => setViewMode('semanal')}
              className={`px-4 py-1.5 rounded-md text-sm font-medium transition ${
                viewMode === 'semanal'
                  ? 'bg-white text-slate-900 shadow'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              Semanal
            </button>
          </div>

          {viewMode !== 'semanal' && (
            <div className="flex items-center gap-3">
              <select
                value={year}
                onChange={(e) => setYear(parseInt(e.target.value, 10))}
                className="px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-fycom-blue outline-none bg-white"
              >
                {YEARS.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
              <select
                value={month}
                onChange={(e) => setMonth(parseInt(e.target.value, 10))}
                className="px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-fycom-blue outline-none bg-white"
              >
                {MONTHS.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          {viewMode === 'semanal' && (
            <div className="flex items-center gap-3">
              <select
                value={year}
                onChange={(e) => setYear(parseInt(e.target.value, 10))}
                className="px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-fycom-blue outline-none bg-white"
              >
                {YEARS.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
              <select
                value={week}
                onChange={(e) => setWeek(parseInt(e.target.value, 10))}
                className="px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-fycom-blue outline-none bg-white min-w-[280px]"
              >
                {weeks.map((w) => (
                  <option key={w.week} value={w.week}>
                    {w.label}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      </div>

      {viewMode === 'acumulado' && (
        <AcumuladoView employeeId={employeeId} year={year} month={month} today={today} />
      )}
      {viewMode === 'mensual' && (
        <PeriodoUnicoView
          employeeId={employeeId}
          startDate={getFirstDayOfMonth(year, month)}
          endDate={getLastDayOfMonth(year, month)}
          label={`${MONTHS.find((m) => m.value === month)?.label} ${year}`}
          dailyChartTitle={`Datos de ${MONTHS.find((m) => m.value === month)?.label}, ${year}:`}
        />
      )}
      {viewMode === 'semanal' && (
        <PeriodoUnicoView
          employeeId={employeeId}
          startDate={getWeekStartEnd(year, week).startDate}
          endDate={getWeekStartEnd(year, week).endDate}
          label={`Semana ${week}: ${formatDisplayDate(getWeekStartEnd(year, week).startDate)} - ${formatDisplayDate(getWeekStartEnd(year, week).endDate)}`}
          dailyChartTitle={`Semana del ${formatDisplayDate(getWeekStartEnd(year, week).startDate)} al ${formatDisplayDate(getWeekStartEnd(year, week).endDate)}:`}
          dailyChartFirst
        />
      )}
    </div>
  );
}

function AcumuladoView({
  employeeId,
  year,
  month,
  today,
}: {
  employeeId: string;
  year: number;
  month: number;
  today: Date;
}) {
  const [monthReport, setMonthReport] = useState<EmployeeReport | null>(null);
  const [yearReport, setYearReport] = useState<EmployeeReport | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Mes/año elegidos = mes actual → corte "a la fecha" (hasta hoy).
  // Mes/año pasado → mes cerrado completo, y el año corre hasta fin de ese mes.
  const isCurrentMonth = year === today.getFullYear() && month === today.getMonth() + 1;
  const monthEnd = isCurrentMonth ? formatDate(today) : getLastDayOfMonth(year, month);
  const monthStart = getFirstDayOfMonth(year, month);
  const yearStart = `${year}-01-01`;
  const yearEnd = monthEnd;

  const monthLabel = `${MONTHS.find((m) => m.value === month)?.label} ${year} (acumulado)`;
  const yearLabel = `${year} (acumulado)`;

  // % de avance del calendario (no de cumplimiento): cuántos días del mes/año
  // ya pasaron respecto a HOY. Mes/año futuro = 0%; pasado = 100%; actual corta hoy.
  const todayStr = formatDate(today);
  const daysBetween = (a: string, b: string) =>
    Math.round((new Date(b + 'T00:00:00').getTime() - new Date(a + 'T00:00:00').getTime()) / 86400000) + 1;
  const elapsedUpTo = (start: string, end: string) => {
    if (todayStr < start) return 0; // período futuro: nada ha transcurrido
    const cutoff = todayStr < end ? todayStr : end; // período pasado: corta al final
    return daysBetween(start, cutoff);
  };

  const monthTotalDays = daysBetween(monthStart, getLastDayOfMonth(year, month));
  const monthCalendarPct = elapsedUpTo(monthStart, getLastDayOfMonth(year, month)) / monthTotalDays;

  const yearTotalDays = daysBetween(yearStart, `${year}-12-31`);
  const yearCalendarPct = elapsedUpTo(yearStart, `${year}-12-31`) / yearTotalDays;

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [monthData, yearData] = await Promise.all([
        fetchEmployeeReport(employeeId, monthStart, monthEnd),
        fetchEmployeeReport(employeeId, yearStart, yearEnd),
      ]);
      setMonthReport(monthData);
      setYearReport(yearData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error desconocido');
    } finally {
      setIsLoading(false);
    }
  }, [employeeId, monthStart, monthEnd, yearStart, yearEnd]);

  useEffect(() => {
    load();
  }, [load]);

  if (isLoading) {
    return <div className="text-center py-12 text-slate-600">Cargando desempeño...</div>;
  }

  if (error) {
    return <div className="bg-red-50 text-fycom-red px-4 py-3 rounded-lg">{error}</div>;
  }

  if (!monthReport && !yearReport) {
    return (
      <div className="text-center py-12 text-slate-500">
        No hay datos de desempeño para este colaborador en el período acumulado.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <PerformancePeriodCard title={monthLabel} report={monthReport} calendarPct={monthCalendarPct} />
        <PerformancePeriodCard title={yearLabel} report={yearReport} calendarPct={yearCalendarPct} />
      </div>

      <DrillDown
        employeeId={employeeId}
        startDate={monthStart}
        endDate={monthEnd}
        periodLabel={monthLabel}
      />
    </div>
  );
}

function PeriodoUnicoView({
  employeeId,
  startDate,
  endDate,
  label,
  dailyChartTitle,
  dailyChartFirst,
}: {
  employeeId: string;
  startDate: string;
  endDate: string;
  label: string;
  dailyChartTitle?: string;
  dailyChartFirst?: boolean;
}) {
  const [report, setReport] = useState<EmployeeReport | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await fetchEmployeeReport(employeeId, startDate, endDate);
      setReport(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error desconocido');
    } finally {
      setIsLoading(false);
    }
  }, [employeeId, startDate, endDate]);

  useEffect(() => {
    load();
  }, [load]);

  if (isLoading) {
    return <div className="text-center py-12 text-slate-600">Cargando desempeño...</div>;
  }

  if (error) {
    return <div className="bg-red-50 text-fycom-red px-4 py-3 rounded-lg">{error}</div>;
  }

  if (!report) {
    return (
      <div className="text-center py-12 text-slate-500">
        No hay datos de desempeño para este colaborador en {label}.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="max-w-md">
        <PerformancePeriodCard title={label} report={report} />
      </div>
      <DrillDown
        employeeId={employeeId}
        startDate={startDate}
        endDate={endDate}
        periodLabel={label}
        acotarGraficoHoras
        dailyChartTitle={dailyChartTitle}
        dailyChartFirst={dailyChartFirst}
      />
    </div>
  );
}

// Drill-down completo (mismo detalle que ve Admin al hacer click en un
// colaborador): disponibilidad semanal (si el rango es 1 semana), tabla de
// registros del rango, y los 2 gráficos históricos.
// `acotarGraficoHoras`: si es true, "Horas por día" muestra EXACTAMENTE los
// días del rango (semana/mes elegido) en vez de la ventana fija de 30 días.
// `dailyChartTitle`/`dailyChartFirst`: usados en modo Semanal para renombrar
// el gráfico ("Semana del X al Y") y mostrarlo antes de la disponibilidad.
function DrillDown({
  employeeId,
  startDate,
  endDate,
  periodLabel,
  acotarGraficoHoras,
  dailyChartTitle,
  dailyChartFirst,
}: {
  employeeId: string;
  startDate: string;
  endDate: string;
  periodLabel: string;
  acotarGraficoHoras?: boolean;
  dailyChartTitle?: string;
  dailyChartFirst?: boolean;
}) {
  const detail = useMemo(
    () =>
      computeEmployeeDetail(
        employeeId,
        getReportData(),
        { startDate, endDate },
        acotarGraficoHoras ? { startDate, endDate } : undefined
      ),
    [employeeId, startDate, endDate, acotarGraficoHoras]
  );

  const dailyChart = <DailyHoursChart data={detail.dailyHours} title={dailyChartTitle} />;

  return (
    <div className="bg-white rounded-xl shadow p-6 space-y-8">
      {dailyChartFirst && dailyChart}

      {detail.weekAvailability.length > 0 && (
        <WeekAvailability days={detail.weekAvailability} />
      )}

      {!detail.hasData ? (
        <div className="text-center text-slate-500 py-8">
          No hay horas registradas en el período cargado.
        </div>
      ) : (
        <>
          <EntriesTable entries={detail.periodEntries} periodLabel={periodLabel} />
          <ClientHoursCharts entries={detail.periodEntries} />
          {!dailyChartFirst && dailyChart}
          <WeeklyTrendChart data={detail.weeklyTrend} />
        </>
      )}
    </div>
  );
}

// Barra de AVANCE DEL CALENDARIO (cuánto del mes/año ya transcurrió), no de
// cumplimiento de horas — esa info ya está en las cards de arriba.
function CalendarProgressBar({ pct }: { pct: number }) {
  const width = Math.min(100, Math.round(pct * 100));

  return (
    <div>
      <div className="flex justify-between text-xs text-slate-500 mb-1">
        <span>Avance del período</span>
        <span>{formatPct(pct)}</span>
      </div>
      <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
        <div className="h-full rounded-full bg-fycom-blue" style={{ width: `${width}%` }} />
      </div>
    </div>
  );
}

function PerformancePeriodCard({
  title,
  report,
  calendarPct,
}: {
  title: string;
  report: EmployeeReport | null;
  calendarPct?: number;
}) {
  return (
    <div className="bg-white rounded-xl shadow overflow-hidden border border-slate-200">
      <div className="px-4 py-2 bg-fycom-navy text-white font-semibold capitalize">
        {title}
      </div>
      {!report ? (
        <div className="p-4 text-sm text-slate-500">Sin datos para este período.</div>
      ) : (
        <div className="p-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className={`rounded-lg px-3 py-2 ${cellColor(report.pctTotal)}`}>
              <div className="text-[10px] font-medium uppercase tracking-wide">
                Cumplimiento Total
              </div>
              <div className="text-xl font-bold leading-tight">
                {formatPct(report.pctTotal)}
              </div>
            </div>
            <div className={`rounded-lg px-3 py-2 ${cellColor(report.pctBillable)}`}>
              <div className="text-[10px] font-medium uppercase tracking-wide">
                Cumplimiento Facturado
              </div>
              <div className="text-xl font-bold leading-tight">
                {formatPct(report.pctBillable)}
              </div>
            </div>
          </div>
          {calendarPct !== undefined && <CalendarProgressBar pct={calendarPct} />}
          <dl className="text-sm text-slate-600 space-y-1">
            <div className="flex justify-between">
              <dt>Horas trabajadas</dt>
              <dd className="font-medium text-slate-900">
                {report.actualTotalHours.toFixed(1)} / {report.expectedTotalHours.toFixed(1)}
              </dd>
            </div>
            <div className="flex justify-between">
              <dt>Horas facturables</dt>
              <dd className="font-medium text-slate-900">
                {report.actualBillableHours.toFixed(1)} / {report.expectedBillableHours.toFixed(1)}
              </dd>
            </div>
            <div className="flex justify-between">
              <dt>Valor facturado</dt>
              <dd className="font-medium text-slate-900">UF {report.valueUF.toFixed(1)}</dd>
            </div>
          </dl>
        </div>
      )}
    </div>
  );
}
